import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import {
  ArrowRight,
  ArrowLeftRight,
  Plus,
  Edit,
  Trash2,
  ToggleLeft,
  ToggleRight,
  X,
  Loader2,
  RefreshCw,
  ChevronDown,
  AlertTriangle,
  Search,
  AlertCircle,
  MoreVertical,
  Settings,
} from "lucide-react";
import {
  fieldMappingService,
  FieldMapping as FieldMappingType,
  HubSpotProperty,
  CreateFieldMappingRequest,
  ContractField,
} from "../../services/FieldMappingService";
import { useToastContext } from "../../context/ToastContext";

// Revenue fields that should trigger warning when mapping to Company/Contact
const REVENUE_FIELDS = [
  'mrr',
  'monthlyrecurringrevenue',
  'arr',
  'annualrecurringrevenue',
  'tcv',
  'totalcontractvalue',
  'acv',
  'annualcontractvalue',
  'renewablebase',
];

interface FieldMappingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const FieldMappingModal: React.FC<FieldMappingModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { showToast } = useToastContext();
  const [mappings, setMappings] = useState<FieldMappingType[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedObjectType, setSelectedObjectType] = useState<
    "deal" | "company" | "contact"
  >("deal");
  const [contractFields, setContractFields] = useState<ContractField[]>([]);
  const [loadingContractFields, setLoadingContractFields] = useState(false);
  const [statusFilter, setStatusFilter] = useState<boolean | "all">("all");
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  const statusFilterRef = useRef<HTMLDivElement>(null);
  const [isAddMappingOpen, setIsAddMappingOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isOpening, setIsOpening] = useState(false);
  const [editingMapping, setEditingMapping] = useState<FieldMappingType | null>(
    null
  );
  const [hubspotProperties, setHubspotProperties] = useState<
    HubSpotProperty[]
  >([]);
  const [loadingProperties, setLoadingProperties] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirmMapping, setDeleteConfirmMapping] = useState<FieldMappingType | null>(null);
  const [isHubSpotFieldDropdownOpen, setIsHubSpotFieldDropdownOpen] = useState(false);
  const [hubSpotFieldSearch, setHubSpotFieldSearch] = useState("");
  const hubSpotFieldDropdownRef = useRef<HTMLDivElement>(null);
  const [isContractFieldDropdownOpen, setIsContractFieldDropdownOpen] = useState(false);
  const [contractFieldSearch, setContractFieldSearch] = useState("");
  const contractFieldDropdownRef = useRef<HTMLDivElement>(null);
  const [mappingSearch, setMappingSearch] = useState("");
  const [openRowMenu, setOpenRowMenu] = useState<string | null>(null);
  const [menuButtonRef, setMenuButtonRef] = useState<{ element: HTMLElement; id: string } | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  const [formData, setFormData] = useState<CreateFieldMappingRequest>({
    contractField: "",
    hubspotField: "",
    hubspotObjectType: "deal",
    status: true,
    syncOnCreate: true, // Default to true, not shown in UI
    syncOnUpdate: true, // Default to true, not shown in UI
  });

  // Load contract fields when modal opens
  useEffect(() => {
    if (isOpen) {
      loadContractFields();
      loadMappings();
    }
  }, [isOpen]);

  // Load mappings when object type or status filter changes
  useEffect(() => {
    if (isOpen) {
      loadMappings();
    }
  }, [selectedObjectType, statusFilter, isOpen]);

  // Close status filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        statusFilterRef.current &&
        !statusFilterRef.current.contains(event.target as Node)
      ) {
        setIsStatusFilterOpen(false);
      }
      if (
        hubSpotFieldDropdownRef.current &&
        !hubSpotFieldDropdownRef.current.contains(event.target as Node)
      ) {
        setIsHubSpotFieldDropdownOpen(false);
      }
      if (
        contractFieldDropdownRef.current &&
        !contractFieldDropdownRef.current.contains(event.target as Node)
      ) {
        setIsContractFieldDropdownOpen(false);
      }
      // Close row menu when clicking outside
      setOpenRowMenu(null);
      setMenuButtonRef(null);
    };

    if (isStatusFilterOpen || isHubSpotFieldDropdownOpen || isContractFieldDropdownOpen || openRowMenu) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isStatusFilterOpen, isHubSpotFieldDropdownOpen, isContractFieldDropdownOpen, openRowMenu]);

  // Load HubSpot properties when modal opens or object type changes
  useEffect(() => {
    if (isAddMappingOpen && !isClosing && isOpen) {
      loadHubSpotProperties();
    }
  }, [isAddMappingOpen, formData.hubspotObjectType, selectedObjectType, isOpen]);


  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen && !isAddMappingOpen) {
        handleCloseModal();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, isAddMappingOpen]);

  const loadContractFields = async () => {
    setLoadingContractFields(true);
    try {
      const response = await fieldMappingService.getContractFields();
      setContractFields(response.data.contractFields);
    } catch (error: any) {
      console.error("Error loading contract fields:", error);
      showToast("error", error.message || "Failed to load contract fields");
    } finally {
      setLoadingContractFields(false);
    }
  };

  const loadMappings = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fieldMappingService.getFieldMappings({
        hubspotObjectType: selectedObjectType,
        status: statusFilter === "all" ? undefined : statusFilter,
      });
      setMappings(response.data.mappings);
    } catch (error: any) {
      console.error("Error loading mappings:", error);
      const errorMessage = error.message || "Failed to load field mappings";
      setError(errorMessage);
      showToast("error", errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const loadHubSpotProperties = async () => {
    setLoadingProperties(true);
    try {
      const response = await fieldMappingService.getHubSpotProperties(
        formData.hubspotObjectType || selectedObjectType
      );
      setHubspotProperties(response.data.properties);
    } catch (error: any) {
      console.error("Error loading HubSpot properties:", error);
      setError(error.message || "Failed to load HubSpot properties");
    } finally {
      setLoadingProperties(false);
    }
  };

  const handleCloseModal = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
      setError(null);
      setSelectedObjectType("deal");
      setStatusFilter("all");
      setMappings([]);
    }, 200);
  };

  const handleClose = () => {
    if (isClosing) return;
    setIsClosing(true);
    setTimeout(() => {
      setIsAddMappingOpen(false);
      setIsClosing(false);
      setEditingMapping(null);
      setError(null);
      setFormData({
        contractField: "",
        hubspotField: "",
        hubspotObjectType: selectedObjectType,
        status: true,
        syncOnCreate: true,
        syncOnUpdate: true,
      });
      setHubSpotFieldSearch("");
      setIsHubSpotFieldDropdownOpen(false);
      setContractFieldSearch("");
      setIsContractFieldDropdownOpen(false);
    }, 150);
  };

  const handleOpenAdd = () => {
    setIsClosing(false);
    setIsOpening(true);
    setIsAddMappingOpen(true);
      setFormData({
        contractField: "",
        hubspotField: "",
        hubspotObjectType: selectedObjectType,
        status: true,
        syncOnCreate: true,
        syncOnUpdate: true,
      });
      setError(null);
      setContractFieldSearch("");
      setIsContractFieldDropdownOpen(false);
      setTimeout(() => {
        setIsOpening(false);
      }, 10);
  };

  const handleEdit = (mapping: FieldMappingType) => {
    setEditingMapping(mapping);
    setFormData({
      contractField: mapping.contractField,
      hubspotField: mapping.hubspotField,
      hubspotObjectType: mapping.hubspotObjectType,
      status: mapping.status,
      syncOnCreate: mapping.syncOnCreate ?? true,
      syncOnUpdate: mapping.syncOnUpdate ?? true,
    });
    setSelectedObjectType(mapping.hubspotObjectType);
    setIsClosing(false);
    setIsOpening(true);
    setIsAddMappingOpen(true);
    setTimeout(() => {
      setIsOpening(false);
    }, 10);
  };

  const handleDelete = (mapping: FieldMappingType) => {
    setDeleteConfirmMapping(mapping);
  };

  const confirmDelete = async () => {
    if (!deleteConfirmMapping) return;

    try {
      await fieldMappingService.deleteFieldMapping(deleteConfirmMapping._id);
      showToast("success", "Field mapping deleted successfully");
      await loadMappings();
      setDeleteConfirmMapping(null);
    } catch (error: any) {
      console.error("Error deleting mapping:", error);
      const errorMessage = error.message || "Failed to delete field mapping";
      showToast("error", errorMessage);
    }
  };

  const handleToggleStatus = async (mapping: FieldMappingType) => {
    const previousStatus = mapping.status;
    // Optimistic update
    setMappings((prev) =>
      prev.map((m) =>
        m._id === mapping._id ? { ...m, status: !m.status } : m
      )
    );

    try {
      await fieldMappingService.updateFieldMapping(mapping._id, {
        status: !mapping.status,
      });
      showToast(
        `Field mapping ${!mapping.status ? "activated" : "deactivated"}`,
        "success"
      );
      await loadMappings();
    } catch (error: any) {
      // Rollback on error
      setMappings((prev) =>
        prev.map((m) =>
          m._id === mapping._id ? { ...m, status: previousStatus } : m
        )
      );
      console.error("Error updating mapping:", error);
      const errorMessage = error.message || "Failed to update field mapping";
      showToast("error", errorMessage);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      if (editingMapping) {
        await fieldMappingService.updateFieldMapping(
          editingMapping._id,
          formData
        );
      } else {
        await fieldMappingService.createFieldMapping(formData);
      }
      await loadMappings();
      showToast(
        "success",
        editingMapping
          ? "Field mapping updated successfully"
          : "Field mapping created successfully"
      );
      handleClose();
    } catch (error: any) {
      console.error("Error saving mapping:", error);
      let errorMessage = "Failed to save field mapping";
      if (error.response?.status === 409) {
        errorMessage =
          "A mapping already exists for this contract field, HubSpot field, and object type combination.";
      } else {
        errorMessage =
          error?.response?.data?.message ||
          error.message ||
          "Failed to save field mapping";
      }
      setError(errorMessage);
      showToast("error", errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleContractFieldSelect = (field: string) => {
    setFormData({ ...formData, contractField: field });
    setIsContractFieldDropdownOpen(false);
    setContractFieldSearch("");
  };

  // Check if we should show the multiple contracts warning
  const shouldShowWarning = (): boolean => {
    if (!formData.contractField || !formData.hubspotObjectType) {
      return false;
    }
    
    const isCompanyOrContact = 
      formData.hubspotObjectType === 'company' || 
      formData.hubspotObjectType === 'contact';
    
    const isRevenueField = REVENUE_FIELDS.includes(
      formData.contractField.toLowerCase()
    );
    
    return isCompanyOrContact && isRevenueField;
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-md z-[100]"
        style={{
          opacity: isClosing ? 0 : 1,
          transition: "opacity 0.2s cubic-bezier(0.4, 0, 1, 1)",
        }}
        onClick={handleCloseModal}
      />

      {/* Modal - Apollo Style (Centered, Large) */}
      <div
        ref={modalRef}
        className="fixed inset-0 z-[101] flex items-center justify-center p-4 pointer-events-none"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="bg-[#0F0F0F] rounded-xl shadow-2xl w-full max-w-7xl max-h-[95vh] flex flex-col pointer-events-auto border border-[#2A2A2A]"
          style={{
            transform: isClosing ? "scale(0.95)" : "scale(1)",
            opacity: isClosing ? 0 : 1,
            transition: "transform 0.2s cubic-bezier(0.4, 0, 1, 1), opacity 0.2s cubic-bezier(0.4, 0, 1, 1)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header - Apollo Style */}
          <div className="flex items-center justify-between px-8 py-5 border-b border-[#2A2A2A] bg-[#0F0F0F]">
            <div>
              <h2 className="text-2xl font-semibold text-white">
                HubSpot Field Mappings
              </h2>
            </div>
            <button
              onClick={handleCloseModal}
              className="p-2 hover:bg-[#1A1A1A] rounded-lg transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5 text-[#8B8B8B]" />
            </button>
          </div>

          {/* Content - Scrollable */}
          <div className="flex-1 overflow-y-auto overflow-x-visible bg-[#0F0F0F]">
            {/* Object Type Tabs - Apollo Style (Top Navigation) */}
            <div className="px-8 pt-4 border-b border-[#2A2A2A] bg-[#0F0F0F]">
              <div className="flex items-center gap-1">
                {(
                  [
                    { value: "deal", label: "Deals", description: "Most common for RevSync" },
                    { value: "company", label: "Companies" },
                    { value: "contact", label: "Contacts" },
                  ] as const
                ).map((type) => (
                  <button
                    key={type.value}
                    onClick={() => setSelectedObjectType(type.value)}
                    className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                      selectedObjectType === type.value
                        ? "border-white text-white bg-[#1A1A1A]"
                        : "border-transparent text-[#8B8B8B] hover:text-white"
                    }`}
                    title={type.description}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Main Content Area */}
            <div className="px-8 py-6 relative">

              {/* Section 01: Search and Actions Bar - Apollo Style */}
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-[#6b7280] text-sm font-medium">01</span>
                  <h3 className="text-lg font-semibold text-white">Map your contract fields</h3>
                </div>
                <p className="text-sm text-[#9ca3af] mb-4 ml-8">
                  Connect RevSync contract fields to HubSpot {selectedObjectType} properties
                </p>
                
                  {/* Search and Action Bar */}
                <div className="flex items-center gap-3 mb-6">
                  <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8B8B8B]" />
                    <input
                      type="text"
                      value={mappingSearch}
                      onChange={(e) => setMappingSearch(e.target.value)}
                      placeholder="Search mappings..."
                      className="w-full pl-9 pr-3 py-2 bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg text-sm text-white placeholder-[#8B8B8B] focus:outline-none focus:ring-2 focus:ring-[#F57E0C]/20 focus:border-[#F57E0C]/50 transition-colors"
                    />
                  </div>
                  
                  <div className="relative" ref={statusFilterRef}>
                    <button
                      onClick={() => setIsStatusFilterOpen(!isStatusFilterOpen)}
                      className="px-3 py-2 text-sm font-medium bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg text-white hover:bg-[#141414] transition-colors flex items-center gap-2 min-w-[120px] justify-between"
                    >
                      <span>
                        {statusFilter === "all"
                          ? "All Status"
                          : statusFilter
                          ? "Active"
                          : "Inactive"}
                      </span>
                      <ChevronDown
                        className={`w-4 h-4 transition-transform ${
                          isStatusFilterOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>

                    {isStatusFilterOpen && (
                      <div className="absolute top-full left-0 mt-2 bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg shadow-lg z-50 min-w-[120px] overflow-hidden">
                        {[
                          { value: "all", label: "All Status" },
                          { value: "true", label: "Active" },
                          { value: "false", label: "Inactive" },
                        ].map((option) => {
                          const isSelected =
                            (option.value === "all" && statusFilter === "all") ||
                            (option.value === "true" && statusFilter === true) ||
                            (option.value === "false" && statusFilter === false);
                          return (
                            <button
                              key={option.value}
                              onClick={() => {
                                setStatusFilter(
                                  option.value === "all"
                                    ? "all"
                                    : option.value === "true"
                                );
                                setIsStatusFilterOpen(false);
                              }}
                              className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                                isSelected
                                  ? "bg-[#141414] text-white font-medium"
                                  : "text-white hover:bg-[#141414]"
                              }`}
                            >
                              {option.label}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <button
                    onClick={() => loadMappings()}
                    className="p-2 bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg text-[#8B8B8B] hover:text-white hover:bg-[#141414] transition-colors"
                    title="Refresh mappings"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </button>
                  
                  <button
                    onClick={handleOpenAdd}
                    className="p-2 bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg text-[#8B8B8B] hover:text-white hover:bg-[#141414] transition-colors"
                    title="Add new mapping"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="mb-6 p-4 bg-red-900/20 border border-red-800/30 rounded-lg">
                  <p className="text-sm text-red-400">{error}</p>
                </div>
              )}

              {/* Field Mapping Table - Apollo Style */}
              <div className="bg-[#0F0F0F] rounded-lg border border-[#2A2A2A] overflow-visible mb-6">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-[#F57E0C]" />
                  <span className="ml-3 text-sm text-[#8B8B8B]">
                    Loading mappings...
                  </span>
                </div>
              ) : mappings.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 px-6">
                  <div className="mb-4 p-3 bg-[#1A1A1A] rounded-full">
                    <ArrowLeftRight className="w-6 h-6 text-[#8B8B8B]" />
                  </div>
                  <p className="text-sm text-[#8B8B8B] mb-2 text-center">
                    No field mappings found for {selectedObjectType}
                  </p>
                  <p className="text-xs text-[#8B8B8B] mb-6 text-center max-w-md">
                    Create your first mapping to start syncing contract fields to HubSpot
                  </p>
                  <button
                    onClick={handleOpenAdd}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-[#1A1A1A] hover:bg-[#141414] border border-[#2A2A2A] text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Add Mapping
                  </button>
                </div>
              ) : (
                <div className="overflow-x-auto relative">
                  <table className="w-full table-auto border-collapse">
                    <thead>
                      <tr className="border-b border-[#2A2A2A] bg-[#0F0F0F]">
                        <th className="text-left py-3 px-6 text-xs font-semibold text-[#8B8B8B] uppercase tracking-wider">
                          RevSync Contract Field
                        </th>
                        <th className="text-left py-3 px-6 text-xs font-semibold text-[#8B8B8B] uppercase tracking-wider">
                          →
                        </th>
                        <th className="text-left py-3 px-6 text-xs font-semibold text-[#8B8B8B] uppercase tracking-wider">
                          HubSpot {selectedObjectType.charAt(0).toUpperCase() + selectedObjectType.slice(1)} Field
                        </th>
                        <th className="text-left py-3 px-6 text-xs font-semibold text-[#8B8B8B] uppercase tracking-wider">
                          Status
                        </th>
                        <th className="text-right py-3 px-6 text-xs font-semibold text-[#8B8B8B] uppercase tracking-wider">
                          
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {mappings
                        .filter((mapping) => {
                          if (!mappingSearch) return true;
                          const searchLower = mappingSearch.toLowerCase();
                          return (
                            mapping.contractField.toLowerCase().includes(searchLower) ||
                            mapping.hubspotField.toLowerCase().includes(searchLower) ||
                            mapping.hubspotObjectType.toLowerCase().includes(searchLower)
                          );
                        })
                        .map((mapping, index) => {
                          const contractField = contractFields.find(f => f.name === mapping.contractField);
                          const hubspotProperty = hubspotProperties.find(p => p.name === mapping.hubspotField);
                          return (
                            <tr
                              key={mapping._id}
                              className={`border-b border-[#2A2A2A] ${
                                index === mappings.length - 1 ? "border-b-0" : ""
                              } hover:bg-[#1A1A1A]/50 transition-colors`}
                            >
                              <td className="py-4 px-6">
                                <div className="flex flex-col">
                                  <span className="text-sm font-medium text-white">
                                    {contractField?.label || mapping.contractField}
                                  </span>
                                  <span className="text-xs text-[#8B8B8B] font-mono mt-0.5">
                                    {mapping.contractField}
                                  </span>
                                </div>
                              </td>
                              <td className="py-4 px-6">
                                <ArrowLeftRight className="w-4 h-4 text-[#8B8B8B]" />
                              </td>
                              <td className="py-4 px-6">
                                <div className="flex flex-col">
                                  <span className="text-sm font-medium text-white">
                                    {hubspotProperty?.label || mapping.hubspotField}
                                  </span>
                                  <span className="text-xs text-[#8B8B8B] font-mono mt-0.5">
                                    {mapping.hubspotField}
                                  </span>
                                </div>
                              </td>
                              <td className="py-4 px-6">
                                <button
                                  onClick={() => handleToggleStatus(mapping)}
                                  className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                                >
                                  <div
                                    className={`w-2 h-2 rounded-full ${
                                      mapping.status
                                        ? "bg-green-500"
                                        : "bg-[#8B8B8B]"
                                    }`}
                                  />
                                  <span
                                    className={`text-sm ${
                                      mapping.status
                                        ? "text-white"
                                        : "text-[#8B8B8B]"
                                    }`}
                                  >
                                    {mapping.status ? "Active" : "Inactive"}
                                  </span>
                                </button>
                              </td>
                              <td className="py-4 px-6 relative">
                                <div className="flex items-center justify-end">
                                  <div className="relative">
                                    <button
                                      ref={(el) => {
                                        if (el) {
                                          // Store ref when button is rendered
                                        }
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (openRowMenu === mapping._id) {
                                          setOpenRowMenu(null);
                                          setMenuButtonRef(null);
                                        } else {
                                          setOpenRowMenu(mapping._id);
                                          setMenuButtonRef({ element: e.currentTarget, id: mapping._id });
                                        }
                                      }}
                                      className="p-1.5 hover:bg-[#1A1A1A] rounded transition-colors text-[#8B8B8B] hover:text-white"
                                    >
                                      <MoreVertical className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              )}
              </div>
            </div>
          </div>

          {/* Footer - Apollo Style */}
          <div className="px-8 py-4 border-t border-[#2A2A2A] bg-[#0F0F0F]">
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={handleCloseModal}
                className="px-4 py-2 text-sm font-medium text-[#8B8B8B] hover:text-white transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Add/Edit Mapping Modal - Nested */}
      {isAddMappingOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-md z-[102]"
            style={{
              opacity: isClosing ? 0 : isOpening ? 0 : 1,
              pointerEvents: isClosing ? "none" : "auto",
              transition: isClosing
                ? "opacity 0.2s cubic-bezier(0.4, 0, 1, 1)"
                : "opacity 0.3s cubic-bezier(0, 0, 0.2, 1)",
            }}
            onClick={handleClose}
          />

          <div
            className="fixed inset-y-0 right-0 w-full max-w-2xl bg-[#0F0F0F] border-l border-[#2A2A2A] shadow-2xl z-[103] overflow-y-auto"
            style={{
              transform: isOpening
                ? "translateX(100%)"
                : isClosing
                ? "translateX(100%)"
                : "translateX(0)",
              opacity: isClosing ? 0 : 1,
              transition: isClosing
                ? "transform 0.2s cubic-bezier(0.4, 0, 1, 1), opacity 0.2s cubic-bezier(0.4, 0, 1, 1)"
                : "transform 0.3s cubic-bezier(0, 0, 0.2, 1), opacity 0.3s cubic-bezier(0, 0, 0.2, 1)",
            }}
          >
            <form onSubmit={handleSubmit} className="flex flex-col h-full">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-[#2A2A2A]">
                <div>
                  <h2 className="text-xl font-semibold text-white">
                    {editingMapping
                      ? "Edit Field Mapping"
                      : "Add Field Mapping"}
                  </h2>
                  <p className="text-sm text-[#8B8B8B] mt-1">
                    {editingMapping
                      ? "Update the mapping between RevSync contract fields and HubSpot properties"
                      : "Create a new mapping from RevSync contract fields to HubSpot properties"}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleClose}
                  className="p-2 hover:bg-[#1A1A1A] rounded-lg transition-colors"
                  aria-label="Close"
                >
                  <X className="w-5 h-5 text-[#8B8B8B]" />
                </button>
              </div>

              {/* Form Content */}
              <div className="flex-1 px-6 py-6 overflow-y-auto">
                <div className="space-y-6">
                  {/* Contract Field - First (what we're mapping FROM) */}
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      RevSync Contract Field
                    </label>
                    {loadingContractFields ? (
                      <div className="flex items-center gap-2 px-4 py-2.5 bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg">
                        <Loader2 className="w-4 h-4 animate-spin text-[#F57E0C]" />
                        <span className="text-sm text-[#8B8B8B]">
                          Loading contract fields...
                        </span>
                      </div>
                    ) : (
                      <div className="relative" ref={contractFieldDropdownRef}>
                        <button
                          type="button"
                          onClick={() => {
                            setIsContractFieldDropdownOpen(!isContractFieldDropdownOpen);
                            if (!isContractFieldDropdownOpen) {
                              setContractFieldSearch("");
                            }
                          }}
                          className="w-full flex items-center justify-between px-4 py-2.5 bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg text-sm text-white hover:border-[#F57E0C]/50 transition-colors text-left"
                        >
                          <span className={formData.contractField ? "" : "text-[#8B8B8B]"}>
                            {formData.contractField
                              ? (() => {
                                  const field = contractFields.find((f) => f.name === formData.contractField);
                                  return field ? `${field.label} (${field.name})` : formData.contractField;
                                })()
                              : "Select contract field..."}
                          </span>
                          <ChevronDown
                            className={`w-4 h-4 text-[#8B8B8B] transition-transform flex-shrink-0 ${
                              isContractFieldDropdownOpen ? "rotate-180" : ""
                            }`}
                          />
                        </button>
                        {isContractFieldDropdownOpen && (
                          <div className="absolute z-20 w-full mt-1 bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg shadow-lg overflow-hidden">
                            {/* Search Input */}
                            <div className="p-2 border-b border-[#2A2A2A]">
                              <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8B8B8B]" />
                                <input
                                  type="text"
                                  value={contractFieldSearch}
                                  onChange={(e) => setContractFieldSearch(e.target.value)}
                                  placeholder="Search fields..."
                                  className="w-full pl-9 pr-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] rounded-lg text-sm text-white placeholder-[#8B8B8B] focus:outline-none focus:ring-2 focus:ring-[#F57E0C]/20 focus:border-[#F57E0C]/50"
                                  autoFocus
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </div>
                            </div>
                            {/* Filtered List */}
                            <div className="max-h-64 overflow-y-auto">
                              {(() => {
                                const filtered = contractFields.filter((field) => {
                                  const searchLower = contractFieldSearch.toLowerCase();
                                  return (
                                    field.label.toLowerCase().includes(searchLower) ||
                                    field.name.toLowerCase().includes(searchLower) ||
                                    (field.description && field.description.toLowerCase().includes(searchLower))
                                  );
                                });

                                if (filtered.length === 0) {
                                  return (
                                    <div className="px-4 py-8 text-center text-sm text-[#8B8B8B]">
                                      No fields found matching "{contractFieldSearch}"
                                    </div>
                                  );
                                }

                                return filtered.map((field) => (
                                  <button
                                    key={field.name}
                                    type="button"
                                    onClick={() => handleContractFieldSelect(field.name)}
                                    className={`w-full text-left px-4 py-2.5 text-sm hover:bg-[#141414] transition-colors ${
                                      formData.contractField === field.name
                                        ? "bg-[#141414] text-white"
                                        : "text-white"
                                    }`}
                                  >
                                    <div className="font-medium">{field.label}</div>
                                    <div className="text-xs text-[#8B8B8B] font-mono mt-0.5">
                                      {field.name}
                                    </div>
                                    {field.description && (
                                      <div className="text-xs text-[#8B8B8B] mt-0.5">
                                        {field.description}
                                      </div>
                                    )}
                                    {field.isRenewalSpecific && (
                                      <div className="text-xs text-blue-400 mt-0.5">
                                        Renewal-specific
                                      </div>
                                    )}
                                  </button>
                                ));
                              })()}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    <p className="text-xs text-[#8B8B8B] mt-1.5">
                      Select a RevSync contract field to map to HubSpot
                    </p>
                  </div>

                  {/* HubSpot Object Type */}
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      HubSpot Object Type
                    </label>
                    <select
                      value={formData.hubspotObjectType || selectedObjectType}
                      onChange={(e) => {
                        const newObjectType = e.target.value as "deal" | "company" | "contact";
                        setFormData({
                          ...formData,
                          hubspotObjectType: newObjectType,
                          hubspotField: "",
                        });
                        setSelectedObjectType(newObjectType);
                      }}
                      disabled={!!editingMapping}
                      className="w-full px-4 py-2.5 bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#F57E0C]/20 focus:border-[#F57E0C]/50 transition-colors"
                    >
                      <option value="deal">Deal</option>
                      <option value="company">Company</option>
                      <option value="contact">Contact</option>
                    </select>
                    {editingMapping && (
                      <p className="text-xs text-[#8B8B8B] mt-1.5">
                        Object type cannot be changed when editing
                      </p>
                    )}
                  </div>

                  {/* HubSpot Field */}
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      HubSpot Field
                    </label>
                    {loadingProperties ? (
                      <div className="flex items-center gap-2 px-4 py-2.5 bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg">
                        <Loader2 className="w-4 h-4 animate-spin text-[#F57E0C]" />
                        <span className="text-sm text-[#8B8B8B]">
                          Loading properties...
                        </span>
                      </div>
                    ) : (
                      <div className="relative" ref={hubSpotFieldDropdownRef}>
                        <button
                          type="button"
                          onClick={() => {
                            setIsHubSpotFieldDropdownOpen(!isHubSpotFieldDropdownOpen);
                            if (!isHubSpotFieldDropdownOpen) {
                              setHubSpotFieldSearch("");
                            }
                          }}
                          className="w-full flex items-center justify-between px-4 py-2.5 bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg text-sm text-white hover:border-[#F57E0C]/50 transition-colors text-left"
                        >
                          <span className={formData.hubspotField ? "" : "text-[#8B8B8B]"}>
                            {formData.hubspotField
                              ? (() => {
                                  const prop = hubspotProperties.find((p) => p.name === formData.hubspotField);
                                  return prop ? `${prop.label} (${prop.name})` : formData.hubspotField;
                                })()
                              : "Select HubSpot field..."}
                          </span>
                          <ChevronDown
                            className={`w-4 h-4 text-[#8B8B8B] transition-transform flex-shrink-0 ${
                              isHubSpotFieldDropdownOpen ? "rotate-180" : ""
                            }`}
                          />
                        </button>
                        {isHubSpotFieldDropdownOpen && (
                          <div className="absolute z-20 w-full mt-1 bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg shadow-lg overflow-hidden">
                            <div className="p-2 border-b border-[#2A2A2A]">
                              <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8B8B8B]" />
                                <input
                                  type="text"
                                  value={hubSpotFieldSearch}
                                  onChange={(e) => setHubSpotFieldSearch(e.target.value)}
                                  placeholder="Search fields..."
                                  className="w-full pl-9 pr-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] rounded-lg text-sm text-white placeholder-[#8B8B8B] focus:outline-none focus:ring-2 focus:ring-[#F57E0C]/20 focus:border-[#F57E0C]/50"
                                  autoFocus
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </div>
                            </div>
                            <div className="max-h-64 overflow-y-auto">
                              {(() => {
                                const filtered = hubspotProperties.filter((prop) => {
                                  const searchLower = hubSpotFieldSearch.toLowerCase();
                                  return (
                                    prop.label.toLowerCase().includes(searchLower) ||
                                    prop.name.toLowerCase().includes(searchLower)
                                  );
                                });

                                if (filtered.length === 0) {
                                  return (
                                    <div className="px-4 py-8 text-center text-sm text-[#8B8B8B]">
                                      No fields found matching "{hubSpotFieldSearch}"
                                    </div>
                                  );
                                }

                                return filtered.map((prop) => (
                                  <button
                                    key={prop.name}
                                    type="button"
                                    onClick={() => {
                                      setFormData({
                                        ...formData,
                                        hubspotField: prop.name,
                                      });
                                      setIsHubSpotFieldDropdownOpen(false);
                                      setHubSpotFieldSearch("");
                                    }}
                                    className={`w-full text-left px-4 py-2.5 text-sm hover:bg-[#141414] transition-colors ${
                                      formData.hubspotField === prop.name
                                        ? "bg-[#141414] text-white"
                                        : "text-white"
                                    }`}
                                  >
                                    <div className="font-medium">{prop.label}</div>
                                    <div className="text-xs text-[#8B8B8B] mt-0.5">
                                      {prop.name}
                                    </div>
                                  </button>
                                ));
                              })()}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    <p className="text-xs text-[#8B8B8B] mt-1.5">
                      Select the field from HubSpot
                    </p>
                  </div>

                  {/* Multiple Contracts Warning */}
                  {shouldShowWarning() && (
                    <div className="bg-yellow-900/20 border border-yellow-800/30 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <h4 className="text-sm font-semibold text-yellow-200 mb-2">
                            Multiple Contracts Warning
                          </h4>
                          <p className="text-sm text-yellow-300 mb-2">
                            If this customer has multiple contracts, only the most recently synced contract's value will be shown in this {formData.hubspotObjectType === 'company' ? 'Company' : 'Contact'} field.
                          </p>
                          <p className="text-sm text-yellow-300">
                            For accurate revenue tracking per contract, we recommend mapping to the <strong className="font-semibold">Deal</strong> object instead.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Status */}
                  <div>
                    <label className="block text-sm font-medium text-white mb-3">
                      Status
                    </label>
                    <button
                      type="button"
                      onClick={() =>
                        setFormData({
                          ...formData,
                          status: !formData.status,
                        })
                      }
                      className="flex items-center gap-3 p-4 border border-[#2A2A2A] rounded-lg hover:border-[#F57E0C]/50 transition-colors bg-[#1A1A1A] w-full"
                    >
                      {formData.status ? (
                        <ToggleRight className="w-5 h-5 text-[#F57E0C]" />
                      ) : (
                        <ToggleLeft className="w-5 h-5 text-[#8B8B8B]" />
                      )}
                      <div className="text-left">
                        <span
                          className={`text-sm font-medium ${
                            formData.status
                              ? "text-white"
                              : "text-[#8B8B8B]"
                          }`}
                        >
                          {formData.status ? "Active" : "Inactive"}
                        </span>
                        <p className="text-xs text-[#8B8B8B] mt-0.5">
                          {formData.status
                            ? "Mapping will sync automatically"
                            : "Mapping is disabled"}
                        </p>
                      </div>
                    </button>
                  </div>

                  {/* Error Message */}
                  {error && (
                    <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                      <p className="text-sm text-red-600 dark:text-red-400">
                        {error}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Footer Actions */}
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#2A2A2A] bg-[#0F0F0F]">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 text-sm font-medium text-[#8B8B8B] hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={
                    submitting ||
                    !formData.hubspotField ||
                    !formData.contractField ||
                    !formData.hubspotObjectType
                  }
                  className="px-6 py-2 bg-[#F57E0C] hover:bg-[#FF8F1F] text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {submitting && (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  )}
                  {editingMapping ? "Save Changes" : "Create Mapping"}
                </button>
              </div>
            </form>
          </div>
        </>
      )}

      {/* Row Menu Dropdown - Portal to render outside table */}
      {openRowMenu && menuButtonRef && (() => {
        const buttonRect = menuButtonRef.element.getBoundingClientRect();
        const menuStyle: React.CSSProperties = {
          position: 'fixed',
          top: `${buttonRect.bottom + 4}px`,
          right: `${window.innerWidth - buttonRect.right}px`,
          zIndex: 10000,
        };
        
        const mapping = mappings.find(m => m._id === openRowMenu);
        if (!mapping) return null;

        return createPortal(
          <>
            {/* Backdrop to close menu */}
            <div
              className="fixed inset-0 z-[9999]"
              onClick={() => {
                setOpenRowMenu(null);
                setMenuButtonRef(null);
              }}
            />
            {/* Menu - Higher z-index than backdrop */}
            <div
              style={menuStyle}
              className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg shadow-xl min-w-[120px]"
              onClick={(e) => {
                e.stopPropagation();
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
            >
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const mappingToEdit = mappings.find(m => m._id === openRowMenu);
                  if (mappingToEdit) {
                    handleEdit(mappingToEdit);
                    setOpenRowMenu(null);
                    setMenuButtonRef(null);
                  }
                }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                }}
                className="w-full text-left px-3 py-2 text-sm text-white hover:bg-[#141414] transition-colors flex items-center gap-2 first:rounded-t-lg"
              >
                <Edit className="w-3 h-3" />
                Edit
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const mappingToDelete = mappings.find(m => m._id === openRowMenu);
                  if (mappingToDelete) {
                    handleDelete(mappingToDelete);
                    setOpenRowMenu(null);
                    setMenuButtonRef(null);
                  }
                }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                }}
                className="w-full text-left px-3 py-2 text-sm text-red-400 hover:bg-[#141414] transition-colors flex items-center gap-2 last:rounded-b-lg"
              >
                <Trash2 className="w-3 h-3" />
                Delete
              </button>
            </div>
          </>,
          document.body
        );
      })()}

      {/* Delete Confirmation Modal */}
      {deleteConfirmMapping && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[104]">
          <div className="bg-[#0F0F0F] border border-[#2A2A2A] rounded-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b border-[#2A2A2A]">
              <h2 className="text-xl font-semibold text-white">
                Delete Field Mapping
              </h2>
              <button
                onClick={() => setDeleteConfirmMapping(null)}
                className="p-2 hover:bg-[#1A1A1A] rounded-lg text-[#8B8B8B] hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-red-900/20 rounded-full">
                  <AlertTriangle className="w-6 h-6 text-red-400" />
                </div>
                <div>
                  <h3 className="font-medium text-white mb-2">
                    Delete mapping between "{deleteConfirmMapping.contractField}" and "{deleteConfirmMapping.hubspotField}"?
                  </h3>
                  <p className="text-[#8B8B8B] text-sm">
                    This will stop syncing this contract field to HubSpot. This action cannot be undone.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 border-t border-[#2A2A2A]">
              <button
                onClick={() => setDeleteConfirmMapping(null)}
                className="px-4 py-2 text-[#8B8B8B] hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
              >
                Delete Mapping
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FieldMappingModal;
